/**
 * This component will render CancelRsvpConfirmation modal for user to cancel RSVPd event.
 *
 * @module views/components/CancelRsvpConfirmation
 * @memberof -Common
 */
import './CancelRsvpConfirmation.scss';

import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';

import { EVENT_STYLES } from '@ulta/utils/constants/events';

import * as utils from './CancelRsvpConfirmation';

/**
  * Represents a confirmation modal component
  *
  * @method
  * @param {CancelRsvpConfirmationProps} props - React properties passed from composition
  * @returns CancelRsvpConfirmation
  */
export const CancelRsvpConfirmation = ( props ) => {
  const {
    headerText,
    confirmMessage,
    confirmLabel,
    cancelLabel,
    action,
    invokeMutation,
    eventId,
    eventStyle,
    timeSlot
  } = props;

  const [closeModal, setCloseModal] = useState( false );
  const { closeOverlay } = useOverlay();

  useEffect( () => {
    if( closeModal === true ){
      closeOverlay( { userCancelled: true } );
    }
  }, [closeModal] );

  if( !headerText || !confirmMessage || !confirmLabel ){
    return null;
  }

  return (
    <div className='CancelRsvp'
      aria-label={ headerText }
    >
      <div className='CancelRsvp__contentBlock'>
        <Text
          htmlTag='h2'
          textStyle='title-6'
        >
          { headerText }
        </Text>
        <div className='confirmMessage'>
          <Text
            htmlTag='span'
            textStyle='body-2'
          >
            { confirmMessage }
          </Text>
        </div>
        <div className='CancelRsvp__actions'>
          <Button
            primary
            compact
            label={ cancelLabel }
            onClick={ () => closeOverlay( { userCancelled: true } ) }
          />
          <Button
            secondary
            compact
            withHover
            action={ action }
            label={ confirmLabel }
            onClick={ () => {
              utils.composeCancelRSVPAction(
                { action, eventId, eventStyle, timeSlot },
                { invokeMutation, setCloseModal } );
            } }
          />
        </div>
      </div>
    </div>
  );
};

/**
 * composeCancelRSVPAction function for toggle button
 * @method
 * @param {Object} data - Passing data as an argument
 * @param {Object} methods - Passing invokeMutation method as an argument
 */

export const composeCancelRSVPAction = ( data, methods ) => {
  const { action, eventId, eventStyle, timeSlot } = data || {};
  const { invokeMutation, setCloseModal } = methods || {};
  const { graphql } = action || {};
  const timeSlotProps = eventStyle === EVENT_STYLES?.TIMESLOT ? { eventStyle, timeSlot } : {};

  if( !graphql || !invokeMutation || !setCloseModal ){
    return;
  }

  invokeMutation( {
    graphql: action.graphql,
    customHeaders: action.customHeaders,
    params: action.params,
    variables: {
      moduleParams: {
        eventId,
        ...timeSlotProps
      },
      url: { path: !isServer() && global.location.href }
    }
  } );
  setCloseModal( true );
};

/**
  * Property type definitions
  * @typedef CancelRsvpConfirmationProps
  * @type {object}
  * @property {string} headerText - Set the header text.
  * @property {string} confirmMessage - Set the confirm Message.
  * @property {string} confirmLabel - Set the confirm Label.
  * @property {string} cancelLabel - set the cancel Label.
  * @property {object} action - Set the remove action
  * @property {function} invokeMutation - Set the invoke Mutation
  * @property {boolean} loading - Set the loading
  */
export const propTypes = {
  headerText: PropTypes.string,
  confirmMessage: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  action: PropTypes.object,
  invokeMutation: PropTypes.func,
  eventId: PropTypes.string,
  eventStyle: PropTypes.string,
  timeSlot: PropTypes.string
};

CancelRsvpConfirmation.propTypes = propTypes;
export default CancelRsvpConfirmation;